import { useEffect, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { useChannels } from '@/hooks/useChannels';
import { usePayees, type EntryInput, type PayeeOption } from '@/hooks/usePayablesReceivables';
import { cn } from '@/lib/utils';

function translateEntryError(msg: string): string {
  if (msg.includes('foreign key') || msg.includes('violates foreign key'))
    return 'Contato não encontrado. Recarregue a página e tente novamente.';
  if (msg.includes('duplicate') || msg.includes('unique'))
    return 'Este lançamento já foi cadastrado.';
  if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('new row violates'))
    return 'Permissão insuficiente. Contate o administrador.';
  if (msg.includes('not-null') || msg.includes('null value'))
    return 'Preencha todos os campos obrigatórios.';
  return msg;
}

interface Props {
  mode: 'payable' | 'receivable';
  onClose: () => void;
  onSubmit: (input: EntryInput) => Promise<void>;
}

export function EntryModal({ mode, onClose, onSubmit }: Props) {
  const { channels, defaultChannel } = useChannels();
  const { payees } = usePayees();

  const [payeeName, setPayeeName] = useState('');
  const [selectedPayee, setSelectedPayee] = useState<PayeeOption | null>(null);
  const [showDrop, setShowDrop] = useState(false);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [channelId, setChannelId] = useState<string | null>(defaultChannel?.id ?? null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (defaultChannel && !channelId) setChannelId(defaultChannel.id);
  }, [defaultChannel, channelId]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = payees
    .filter((p) => p.name.toLowerCase().includes(payeeName.toLowerCase()))
    .slice(0, 6);

  const amountCents = Math.round(
    parseFloat(amount.replace(/\./g, '').replace(',', '.')) * 100
  ) || 0;

  const valid =
    payeeName.trim().length > 0 &&
    description.trim().length > 0 &&
    amountCents > 0 &&
    dueDate.length > 0;

  function handlePayeeSelect(p: PayeeOption) {
    setPayeeName(p.name);
    setSelectedPayee(p);
    setShowDrop(false);
  }

  function handlePayeeInput(value: string) {
    setPayeeName(value);
    setSelectedPayee(null);
    setShowDrop(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        payeeName: payeeName.trim(),
        selectedPayeeId: selectedPayee?.id ?? null,
        description: description.trim(),
        amountCents,
        dueDate,
        channelId,
      });
      onClose();
    } catch (err) {
      setError(translateEntryError((err as Error).message));
      setSaving(false);
    }
  }

  const payeeLabel = mode === 'payable' ? 'Fornecedor' : 'Cliente';
  const placeholder = mode === 'payable' ? 'Buscar ou criar fornecedor…' : 'Buscar ou criar cliente…';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md bg-surface rounded-2xl shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-display font-semibold text-base">
            {mode === 'payable' ? 'Nova conta a pagar' : 'Nova conta a receber'}
          </h2>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">

          {/* Payee combobox */}
          <div ref={dropRef} className="relative">
            <label className="block text-xs font-medium text-ink-muted mb-1">
              {payeeLabel} <span className="text-negative">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
              <input
                type="text"
                placeholder={placeholder}
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-border bg-surface-raised text-sm focus:border-brand-500 outline-none"
                value={payeeName}
                onChange={(e) => handlePayeeInput(e.target.value)}
                onFocus={() => setShowDrop(true)}
                autoComplete="off"
              />
            </div>
            {showDrop && payeeName.length > 0 && (
              <div className="absolute z-20 w-full mt-1 bg-surface border border-border rounded-xl shadow-lg overflow-hidden">
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-subtle transition-colors"
                    onMouseDown={(e) => { e.preventDefault(); handlePayeeSelect(p); }}
                  >
                    {p.name}
                  </button>
                ))}
                {filtered.length === 0 && (
                  <div className="px-4 py-2.5 text-sm text-ink-muted">
                    Criar: <span className="font-medium text-brand-700">"{payeeName}"</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">
              Descrição <span className="text-negative">*</span>
            </label>
            <input
              type="text"
              placeholder="Ex: Fatura de fornecedor, Aluguel…"
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-surface-raised text-sm focus:border-brand-500 outline-none"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Amount + Due date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">
                Valor (R$) <span className="text-negative">*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                className={cn(
                  'w-full px-3 py-2.5 rounded-xl border bg-surface-raised text-sm outline-none',
                  amount && amountCents === 0 ? 'border-negative' : 'border-border focus:border-brand-500'
                )}
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">
                Vencimento <span className="text-negative">*</span>
              </label>
              <input
                type="date"
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-surface-raised text-sm focus:border-brand-500 outline-none"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Channel — só mostra se tiver mais de um */}
          {channels.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">Canal</label>
              <select
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-surface-raised text-sm focus:border-brand-500 outline-none"
                value={channelId ?? ''}
                onChange={(e) => setChannelId(e.target.value || null)}
              >
                <option value="">— Sem canal —</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-negative/10 border border-negative/20 text-negative text-sm p-3">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost flex-1" disabled={saving}>
              Cancelar
            </button>
            <button type="submit" disabled={!valid || saving} className="btn-primary flex-1 gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
